import React from "react";
import { useParams } from "react-router-dom";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { readContactCompleteByContactId } from "services/contacts.service";

type Row = {
  id: string;
  address1: string;
  address2: string;
  town: string;
  city: string;
  zip: string;
};

function mapAddressToRow(raw: unknown, index: number): Row {
  const a = raw as Record<string, unknown>;
  const id =
    a.addressId != null || a.AddressId != null
      ? String(a.addressId ?? a.AddressId)
      : `addr-${index}`;
  const str = (camel: string, pascal: string) =>
    a[camel] != null
      ? String(a[camel])
      : a[pascal] != null
        ? String(a[pascal])
        : "";
  return {
    id,
    address1: str("address1", "Address1"),
    address2: str("address2", "Address2"),
    town: str("town", "Town"),
    city: str("city", "City"),
    zip: str("zip", "Zip"),
  };
}

/**
 * Was rendering four hardcoded Stockholm addresses, and its delete button called
 * navigate(id) rather than deleting anything. Now reads the contact's real
 * addressList from ReadContactCompleteByContactID.
 *
 * Delete is intentionally absent: Delete_Address exists on the API, but wiring a
 * destructive action is a change worth making deliberately rather than as part
 * of removing mock data.
 */
const AddressesList = () => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [rows, setRows] = React.useState<Row[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const view = useTableView(rows);
  const contactId = id ? Number(id) : NaN;

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (
        !isAuthenticated ||
        orgId <= 0 ||
        !Number.isFinite(contactId) ||
        contactId <= 0
      ) {
        setRows([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const { data, error: apiError } = await readContactCompleteByContactId(
          token,
          orgId,
          contactId
        );
        if (cancelled) return;
        if (apiError) {
          setError(apiError.message);
          setRows([]);
          return;
        }
        setRows((data?.addressList ?? []).map(mapAddressToRow));
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message);
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated, orgId, contactId]);

  return (
    <div>
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="m-4"
      />

      <Table<Row>
        loading={loading}
        columns={{
          address1: "Address 1",
          address2: "Address 2",
          town: "Town",
          city: "City",
          zip: "Zipcode",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        emptyMessage="No addresses recorded for this client."
      />

      {loading && (
        <div className="px-4 pt-2 text-sm text-slate-500">Loading addresses…</div>
      )}
      {error && <div className="px-4 pt-2 text-sm text-red-500">{error}</div>}

      <PaginationBar
        from={view.from}
        to={view.to}
        total={view.total}
        currentPage={view.page}
        totalPages={view.totalPages}
        onPrev={view.goPrev}
        onNext={view.goNext}
        onPageSelect={view.goToPage}
      />
    </div>
  );
};

export default AddressesList;
